<template>
  <header class="nav">
    <div class="nav-inner" :class="{ 'logged-in': !loadingAuth && user }">
      <!-- Hamburger Button (Mobile) - eigenes Grid-Item für Neuanordnung -->
      <button 
        v-if="!loadingAuth" 
        class="hamburger" 
        :class="{ 'is-open': mobileMenuOpen }" 
        @click="toggleMobileMenu" 
        aria-label="Menü"
      >
        <span class="hamburger-line"></span>
        <span class="hamburger-line"></span>
        <span class="hamburger-line"></span>
      </button>

      <div class="brand">
        <router-link to="/" class="home-link">
          <img class="logo" src="../assets/logo_blau.svg" alt="Logo" />
        </router-link>
      </div>
      <!-- Desktop Links -->
      <nav class="links desktop-links" :class="{ 'links-right': !user }">
        <router-link to="/" class="link">Home</router-link>
        <router-link to="/big-picture" class="link">Big Picture</router-link>
        <template v-if="!loadingAuth">
          <template v-if="user">
            <router-link to="/dashboard" class="link">Dashboard</router-link>
            <router-link to="/scenarios" class="link">Szenarien</router-link>
            <router-link to="/leaderboard" class="link">Bestenliste</router-link>
            <template v-if="userRole === true">
              <router-link 
                to="/admin" 
                class="link"
                :class="{ 'router-link-active': $route.path.startsWith('/admin') }"
              >
                Admin
              </router-link>
            </template>
          </template>
          <template v-else>
            <router-link to="/leaderboard" class="link">Bestenliste</router-link>
            <router-link to="/signin" class="link">Anmelden</router-link>
            <router-link to="/register" class="link">Registrieren</router-link>
          </template>
        </template>
      </nav>

      <div class="nav-right">
        <template v-if="!loadingAuth && user">
          <div class="profile-container" ref="profileContainer">
            <button class="profile-btn" @click="toggleProfileMenu" :disabled="loggingOut">
              <div class="profile-avatar">
                {{ userInitials }}
              </div>
            </button>
            <div v-if="showProfileMenu" class="profile-dropdown">
              <div class="profile-header">
                <div class="profile-avatar-large">{{ userInitials }}</div>
                <div class="profile-info">
                  <div class="profile-name">{{ userProfile?.name || user?.email }}</div>
                  <div class="profile-email">{{ user?.email }}</div>
                </div>
              </div>
              <div class="profile-divider"></div>
              <button class="profile-menu-item" @click="openEditName">
                <span class="menu-icon">
                  <img src="../assets/pen.svg" alt="Edit" class="icon-svg" />
                </span>
                Name ändern
              </button>
              <button class="profile-menu-item" @click="openChangePassword">
                <span class="menu-icon">
                  <img src="../assets/lock.svg" alt="Lock" class="icon-svg" />
                </span>
                Passwort ändern
              </button>
              <div class="profile-divider"></div>
              <button class="profile-menu-item logout-item" @click="handleLogout" :disabled="loggingOut">
                <span class="menu-icon">
                  <img src="../assets/door.svg" alt="Logout" class="icon-svg" />
                </span>
                <span v-if="loggingOut">Abmelden…</span>
                <span v-else>Abmelden</span>
              </button>
              <button class="profile-menu-item delete-item" @click="openDeleteAccount">
                <span class="menu-icon">
                  <img src="../assets/bin.svg" alt="Delete" class="icon-svg" />
                </span>
                Account löschen
              </button>
            </div>
          </div>
        </template>
      </div>
    </div>
  </header>

  <!-- Mobile Menu Overlay - für alle Benutzer -->
  <Transition name="mobile-menu">
    <div v-if="mobileMenuOpen && !loadingAuth" class="mobile-menu-overlay" @click.self="closeMobileMenu">
      <nav class="mobile-menu">
        <router-link to="/" class="mobile-link" @click="closeMobileMenu">
          <span class="mobile-link-icon"><img src="../assets/home.svg" alt="Home" class="mobile-icon-svg" /></span> Home
        </router-link>
        <router-link to="/big-picture" class="mobile-link" @click="closeMobileMenu">
          <span class="mobile-link-icon"><img src="../assets/world.svg" alt="Big Picture" class="mobile-icon-svg" /></span> Big Picture
        </router-link>
        <template v-if="!loadingAuth">
          <template v-if="user">
            <router-link to="/dashboard" class="mobile-link" @click="closeMobileMenu">
              <span class="mobile-link-icon"><img src="../assets/dashboard.svg" alt="Dashboard" class="mobile-icon-svg" /></span> Dashboard
            </router-link>
            <router-link to="/scenarios" class="mobile-link" @click="closeMobileMenu">
              <span class="mobile-link-icon"><img src="../assets/bullseye.svg" alt="Szenarien" class="mobile-icon-svg" /></span> Szenarien
            </router-link>
            <router-link to="/leaderboard" class="mobile-link" @click="closeMobileMenu">
              <span class="mobile-link-icon"><img src="../assets/cup.svg" alt="Bestenliste" class="mobile-icon-svg" /></span> Bestenliste
            </router-link>
            <template v-if="userRole === true">
              <router-link to="/admin" class="mobile-link" @click="closeMobileMenu"
                :class="{ 'router-link-active': $route.path.startsWith('/admin') }">
                <span class="mobile-link-icon"><img src="../assets/settings.svg" alt="Admin" class="mobile-icon-svg" /></span> Admin
              </router-link>
            </template>
          </template>
          <template v-else>
            <router-link to="/leaderboard" class="mobile-link" @click="closeMobileMenu">
              <span class="mobile-link-icon"><img src="../assets/cup.svg" alt="Bestenliste" class="mobile-icon-svg" /></span> Bestenliste
            </router-link>
            <div class="mobile-divider"></div>
            <router-link to="/signin" class="mobile-link" @click="closeMobileMenu">
              <span class="mobile-link-icon"><img src="../assets/lock.svg" alt="Anmelden" class="mobile-icon-svg" /></span> Anmelden
            </router-link>
            <router-link to="/register" class="mobile-link" @click="closeMobileMenu">
              <span class="mobile-link-icon"><img src="../assets/pen.svg" alt="Registrieren" class="mobile-icon-svg" /></span> Registrieren
            </router-link>
          </template>
        </template>
      </nav>
    </div>
  </Transition>

  <!-- Edit Name Modal -->
  <div v-if="showEditNameModal" class="modal-overlay" @click.self="showEditNameModal = false">
    <div class="modal-content">
      <h3>Name ändern</h3>
      <form @submit.prevent="handleUpdateName">
        <label>
          Neuer Name
          <input
            type="text"
            v-model.trim="newName"
            placeholder="Ihr Name"
            required
            autofocus
          />
        </label>
        <p v-if="error" class="error">{{ error }}</p>
        <div class="modal-actions">
          <button type="button" class="btn-secondary" @click="showEditNameModal = false" :disabled="loading">
            Abbrechen
          </button>
          <button type="submit" class="btn-primary" :disabled="loading">
            <span v-if="loading">Speichern…</span>
            <span v-else>Speichern</span>
          </button>
        </div>
      </form>
    </div>
  </div>

  <!-- Change Password Modal -->
  <div v-if="showChangePasswordModal" class="modal-overlay" @click.self="showChangePasswordModal = false">
    <div class="modal-content">
      <h3>Passwort ändern</h3>
      <form @submit.prevent="handleChangePassword">
        <label>
          Aktuelles Passwort
          <div class="password-field">
            <input
              :type="showCurrentPw ? 'text' : 'password'"
              v-model="currentPassword"
              placeholder="••••••••"
              required
              autofocus
            />
            <button type="button" class="eye-btn" @click="showCurrentPw = !showCurrentPw">
              <img v-if="showCurrentPw" src="../assets/eyeclose.svg" alt="Hide" class="eye-icon" />
              <img v-else src="../assets/eyeopen.svg" alt="Show" class="eye-icon" />
            </button>
          </div>
        </label>
        <label>
          Neues Passwort
          <div class="password-field">
            <input
              :type="showNewPw ? 'text' : 'password'"
              v-model="newPassword"
              placeholder="••••••••"
              required
            />
            <button type="button" class="eye-btn" @click="showNewPw = !showNewPw">
              <img v-if="showNewPw" src="../assets/eyeclose.svg" alt="Hide" class="eye-icon" />
              <img v-else src="../assets/eyeopen.svg" alt="Show" class="eye-icon" />
            </button>
          </div>
        </label>
        <label>
          Neues Passwort bestätigen
          <div class="password-field">
            <input
              :type="showConfirmPw ? 'text' : 'password'"
              v-model="confirmPassword"
              placeholder="••••••••"
              required
            />
            <button type="button" class="eye-btn" @click="showConfirmPw = !showConfirmPw">
              <img v-if="showConfirmPw" src="../assets/eyeclose.svg" alt="Hide" class="eye-icon" />
              <img v-else src="../assets/eyeopen.svg" alt="Show" class="eye-icon" />
            </button>
          </div>
        </label>
        <p v-if="error" class="error">{{ error }}</p>
        <div class="modal-actions">
          <button type="button" class="btn-secondary" @click="showChangePasswordModal = false" :disabled="loading">
            Abbrechen
          </button>
          <button type="submit" class="btn-primary" :disabled="loading">
            <span v-if="loading">Ändern…</span>
            <span v-else>Passwort ändern</span>
          </button>
        </div>
      </form>
    </div>
  </div>

  <!-- Delete Account Modal -->
  <div v-if="showDeleteAccountModal" class="modal-overlay" @click.self="showDeleteAccountModal = false">
    <div class="modal-content">
      <h3>Account löschen</h3>
      <p class="warning-text">Diese Aktion kann nicht rückgängig gemacht werden. Alle Ihre Daten werden dauerhaft gelöscht.</p>
      <form @submit.prevent="handleDeleteAccount">
        <label>
          Passwort zur Bestätigung
          <div class="password-field">
            <input
              :type="showDeletePw ? 'text' : 'password'"
              v-model="deletePassword"
              placeholder="••••••••"
              required
              autofocus
            />
            <button type="button" class="eye-btn" @click="showDeletePw = !showDeletePw">
              <img v-if="showDeletePw" src="../assets/eyeclose.svg" alt="Hide" class="eye-icon" />
              <img v-else src="../assets/eyeopen.svg" alt="Show" class="eye-icon" />
            </button>
          </div>
        </label>
        <p v-if="error" class="error">{{ error }}</p>
        <div class="modal-actions">
          <button type="button" class="btn-secondary" @click="showDeleteAccountModal = false" :disabled="loading">
            Abbrechen
          </button>
          <button type="submit" class="btn-danger" :disabled="loading">
            <span v-if="loading">Löschen…</span>
            <span v-else>Account löschen</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth'
import { getCurrentUserProfile, updateUserName, changePassword, deleteAccount } from '../services/userService'

const router = useRouter()
const user = ref(null) // aktuell angemeldeter firebase benutzer
const userRole = ref(null) // rolle des benutzers (null = nicht angemeldet, false = schüler, true = admin)
const userProfile = ref(null) // benutzerprofil aus firestore
const loadingAuth = ref(true) // lade-status für authentifizierung
const loggingOut = ref(false) // status ob logout gerade durchgeführt wird
// reaktive variablen für ui-zustände
const mobileMenuOpen = ref(false) // ob mobiles menü geöffnet ist
const showProfileMenu = ref(false) // ob profil-dropdown geöffnet ist
const showEditNameModal = ref(false) // ob name-ändern modal geöffnet ist
const showChangePasswordModal = ref(false) // ob passwort-ändern modal geöffnet ist
const showDeleteAccountModal = ref(false) // ob account-löschen modal geöffnet ist

// formular-daten für die modals
const newName = ref('') // neuer name für name-ändern
const currentPassword = ref('') // aktuelles passwort für passwort-ändern
const newPassword = ref('') // neues passwort für passwort-ändern
const confirmPassword = ref('') // passwort-bestätigung für passwort-ändern
const deletePassword = ref('') // passwort für account-löschen
// passwort-sichtbarkeit toggles
const showCurrentPw = ref(false)
const showNewPw = ref(false)
const showConfirmPw = ref(false)
const showDeletePw = ref(false)
const error = ref('') // fehlermeldung für die modals
const loading = ref(false) // lade-status für formular-submission
const profileContainer = ref(null) // referenz zum profil-container element (für click-outside detection)

// lifecycle hook: wird aufgerufen wenn die komponente gemountet ist
onMounted(() => {
  // richte click-outside handler ein (schließt profil-menu wenn außerhalb geklickt wird)
  document.addEventListener('click', handleClickOutside)
  
  // richte auth state listener ein (hört auf änderungen des login-status)
  const auth = getAuth()
  onAuthStateChanged(auth, async (firebaseUser) => {
    // aktualisiere benutzer-variable
    user.value = firebaseUser
    
    if (firebaseUser) {
      // benutzer ist angemeldet: lade profil und prüfe rolle
      try {
        const profile = await getCurrentUserProfile()
        userProfile.value = profile
        userRole.value = profile?.role || false // false = schüler, true = admin
      } catch (error) {
        console.error('Error loading user profile:', error)
        // bei fehler setze standardwerte
        userRole.value = false
        userProfile.value = null
      }
    } else {
      // benutzer ist nicht angemeldet: setze alles auf null
      userRole.value = null
      userProfile.value = null
    }
    
    // authentifizierung abgeschlossen, verstecke lade-animation
    loadingAuth.value = false
  })
})

// beobachte änderungen am benutzerprofil
// wenn profil geändert wird und name-ändern modal offen ist, aktualisiere das formular
watch(userProfile, (newProfile) => {
  if (newProfile && showEditNameModal.value) {
    newName.value = newProfile.name || ''
  }
}, { immediate: true })

// berechnet die initialen des benutzers für den profil-avatar
const userInitials = computed(() => {
  if (userProfile.value?.name) {
    const names = userProfile.value.name.split(' ')
    // wenn mindestens 2 namen vorhanden, nimm ersten buchstaben von vor- und nachname
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase()
    }
    // sonst nimm ersten 2 buchstaben des namens
    return userProfile.value.name.substring(0, 2).toUpperCase()
  }
  // wenn kein name vorhanden, verwende ersten 2 buchstaben der email
  if (user.value?.email) {
    return user.value.email.substring(0, 2).toUpperCase()
  }
  // fallback wenn nichts verfügbar
  return '??'
})

// mobile menü toggle
const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value
  if (mobileMenuOpen.value) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
}

const closeMobileMenu = () => {
  mobileMenuOpen.value = false
  document.body.style.overflow = ''
}

// funktion zum umschalten des profil-menus (öffnen/schließen)
const toggleProfileMenu = () => {
  showProfileMenu.value = !showProfileMenu.value
}

// funktion zum öffnen des name-ändern modals
// setzt den aktuellen namen ins formular und schließt das profil-menu
const openEditName = () => {
  newName.value = userProfile.value?.name || ''
  error.value = ''
  showEditNameModal.value = true
  showProfileMenu.value = false
}

// funktion zum öffnen des passwort-ändern modals
// leert alle passwort-felder und schließt das profil-menu
const openChangePassword = () => {
  currentPassword.value = ''
  newPassword.value = ''
  confirmPassword.value = ''
  error.value = ''
  showCurrentPw.value = false
  showNewPw.value = false
  showConfirmPw.value = false
  showChangePasswordModal.value = true
  showProfileMenu.value = false
}

// funktion zum öffnen des account-löschen modals
// leert das passwort-feld und schließt das profil-menu
const openDeleteAccount = () => {
  deletePassword.value = ''
  error.value = ''
  showDeletePw.value = false
  showDeleteAccountModal.value = true
  showProfileMenu.value = false
}

// funktion zum aktualisieren des benutzernamens
// validiert die eingabe und ruft den service auf
const handleUpdateName = async () => {
  error.value = ''
  // prüfe ob ein name eingegeben wurde
  if (!newName.value.trim()) {
    error.value = 'Bitte geben Sie einen Namen ein.'
    return
  }
  
  loading.value = true
  try {
    // aktualisiere den namen über den service
    await updateUserName(newName.value.trim())
    // hole das aktualisierte profil
    const profile = await getCurrentUserProfile()
    userProfile.value = profile
    // schließe das modal und leere das formular
    showEditNameModal.value = false
    newName.value = ''
  } catch (e) {
    // zeige fehlermeldung bei fehler
    error.value = e?.message || 'Fehler beim Aktualisieren des Namens.'
  } finally {
    loading.value = false
  }
}

// funktion zum ändern des passworts
// validiert die eingaben und ruft den service auf
const handleChangePassword = async () => {
  error.value = ''
  // prüfe ob alle felder ausgefüllt sind
  if (!currentPassword.value || !newPassword.value || !confirmPassword.value) {
    error.value = 'Bitte füllen Sie alle Felder aus.'
    return
  }
  
  // prüfe ob die neuen passwörter übereinstimmen
  if (newPassword.value !== confirmPassword.value) {
    error.value = 'Die neuen Passwörter stimmen nicht überein.'
    return
  }
  
  // prüfe minimale passwort-länge
  if (newPassword.value.length < 6) {
    error.value = 'Das Passwort muss mindestens 6 Zeichen lang sein.'
    return
  }
  
  loading.value = true
  try {
    // ändere das passwort über den service
    await changePassword(currentPassword.value, newPassword.value)
    // schließe das modal und leere alle felder
    showChangePasswordModal.value = false
    currentPassword.value = ''
    newPassword.value = ''
    confirmPassword.value = ''
    error.value = ''
    alert('Passwort erfolgreich geändert!')
  } catch (e) {
    // behandle verschiedene fehlercodes
    const code = e?.code || ''
    if (code === 'auth/wrong-password') {
      error.value = 'Aktuelles Passwort ist falsch.'
    } else if (code === 'auth/weak-password') {
      error.value = 'Das Passwort ist zu schwach.'
    } else {
      error.value = e?.message || 'Fehler beim Ändern des Passworts.'
    }
  } finally {
    loading.value = false
  }
}

// funktion zum löschen des accounts
// benötigt passwort-bestätigung und bestätigung vom benutzer
const handleDeleteAccount = async () => {
  error.value = ''
  // prüfe ob passwort eingegeben wurde
  if (!deletePassword.value) {
    error.value = 'Bitte geben Sie Ihr Passwort ein, um den Account zu löschen.'
    return
  }
  
  // frage benutzer zur bestätigung 
  if (!confirm('Sind Sie sicher, dass Sie Ihren Account löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.')) {
    return
  }
  
  loading.value = true
  try {
    // lösche den account über den service
    await deleteAccount(deletePassword.value)
    // schließe das modal
    showDeleteAccountModal.value = false
    // setze alle benutzerdaten auf null
    user.value = null
    userRole.value = null
    userProfile.value = null
    // leite zur startseite um
    router.push('/')
    alert('Ihr Account wurde erfolgreich gelöscht.')
  } catch (e) {
    // behandle fehlercodes
    const code = e?.code || ''
    if (code === 'auth/wrong-password') {
      error.value = 'Passwort ist falsch.'
    } else {
      error.value = e?.message || 'Fehler beim Löschen des Accounts.'
    }
  } finally {
    loading.value = false
  }
}

// funktion zum abmelden des benutzers
// meldet den benutzer ab und leitet zur startseite um
const handleLogout = async () => {
  loggingOut.value = true
  showProfileMenu.value = false
  try {
    // melde benutzer ab über firebase auth
    await signOut(getAuth())
    // setze alle benutzerdaten auf null
    user.value = null
    userRole.value = null
    userProfile.value = null
    // leite zur startseite um
    router.push('/')
  } catch (e) {
    console.error('Logout error:', e)
  } finally {
    loggingOut.value = false
  }
}

// funktion zum behandeln von klicks außerhalb des profil-menus
// schließt das profil-menu wenn außerhalb geklickt wird
const handleClickOutside = (event) => {
  if (profileContainer.value && !profileContainer.value.contains(event.target)) {
    showProfileMenu.value = false
  }
}

// lifecycle hook: wird aufgerufen wenn die komponente unmounted wird
// entfernt den event listener um memory leaks zu vermeiden
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  document.body.style.overflow = ''
})
</script>

<style scoped>
.nav {
  background: #fff;
  border-bottom: 1px solid rgba(0,0,0,0.06);
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  position: sticky;
  top: 0;
  z-index: 1000;
  backdrop-filter: blur(10px);
}

.nav-inner {
  min-height: 70px;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
  gap: 12px;
}

.brand {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.nav-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
}

.home-link {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  transition: transform 0.2s ease;
}

.home-link:hover {
  transform: scale(1.05);
}

.logo {
  height: 44px;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.logo:hover {
  opacity: 0.9;
}

.links {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  justify-content: center;
}

.links-right {
  justify-content: flex-end;
}

.link {
  color: #333;
  text-decoration: none;
  font-size: 15px;
  font-weight: 500;
  padding: 8px 16px;
  border-radius: 8px;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  white-space: nowrap;
}

.link::before {
  content: '';
  position: absolute;
  bottom: 4px;
  left: 50%;
  transform: translateX(-50%) scaleX(0);
  width: 60%;
  height: 2px;
  background: var(--secondary, #0063AD);
  border-radius: 2px;
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.link:hover {
  color: var(--secondary, #0063AD);
  background: rgba(0,0,0,0.06);
}

.link:hover::before,
.link.router-link-active::before {
  transform: translateX(-50%) scaleX(1);
}

.link.router-link-active {
  color: var(--secondary, #0063AD);
  background: rgba(0, 99, 173, 0.12);
}

.divider {
  color: rgba(0,0,0,0.25);
  font-weight: 300;
  user-select: none;
  display: none;
}

.logout-btn {
  color: #333;
  text-decoration: none;
  font-size: 15px;
  font-weight: 500;
  padding: 8px 16px;
  border-radius: 8px;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  background: transparent;
  border: 1px solid rgba(0,0,0,0.1);
  cursor: pointer;
  font-family: inherit;
}

.logout-btn:hover:not(:disabled) {
  color: var(--secondary, #0063AD);
  background: rgba(0,0,0,0.06);
  border-color: rgba(0, 99, 173, 0.2);
}

.logout-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Profile Dropdown */
.profile-container {
  position: relative;
  flex-shrink: 0;
}

.profile-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
}

.profile-btn:hover:not(:disabled) {
  transform: scale(1.05);
}

.profile-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.profile-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0063AD, #234998);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 2px 8px rgba(0, 99, 173, 0.2);
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.profile-dropdown {
  position: absolute;
  top: calc(100% + 12px);
  right: 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  min-width: 280px;
  z-index: 1001;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

@media (max-width: 768px) {
  .profile-dropdown {
    min-width: 260px;
  }
}

@media (max-width: 480px) {
  .profile-dropdown {
    min-width: 240px;
  }
}

.profile-header {
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(135deg, #f7f9fc, #e8f2ff);
}

.profile-avatar-large {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0063AD, #234998);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 18px;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  flex-shrink: 0;
}

.profile-info {
  flex: 1;
  min-width: 0;
}

.profile-name {
  font-weight: 700;
  font-size: 15px;
  color: #234998;
  margin-bottom: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.profile-email {
  font-size: 13px;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.profile-divider {
  height: 1px;
  background: rgba(0, 0, 0, 0.08);
  margin: 4px 0;
}

.profile-menu-item {
  width: 100%;
  padding: 12px 16px;
  background: transparent;
  border: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: #333;
  transition: background 0.2s ease;
  font-family: inherit;
}

.profile-menu-item:hover:not(:disabled) {
  background: rgba(0, 99, 173, 0.08);
  color: #0063AD;
}

.profile-menu-item:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.menu-icon {
  font-size: 18px;
  width: 20px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-svg {
  width: 18px;
  height: 18px;
  display: block;
}

.delete-item .icon-svg {
  width: 27px;
  height: 27px;
}

.logout-item {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  margin-top: 4px;
}

.delete-item {
  color: #dc2626;
}

.delete-item:hover:not(:disabled) {
  background: rgba(220, 38, 38, 0.1);
  color: #dc2626;
}

/* Modals */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: white;
  border-radius: 16px;
  padding: 24px;
  max-width: 480px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-content h3 {
  margin: 0 0 20px 0;
  color: #234998;
  font-size: 24px;
}

.modal-content form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.modal-content label {
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: #1f2937;
  font-weight: 600;
  font-size: 14px;
}

.modal-content input {
  width: 100%;
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid #d7e3f4;
  background: #ffffff;
  color: #111827;
  transition: all 0.2s ease;
  font-size: 15px;
  font-family: inherit;
}

.modal-content input:focus {
  outline: none;
  border-color: #0063AD;
  box-shadow: 0 0 0 3px rgba(0, 99, 173, 0.18);
}

.warning-text {
  background: rgba(220, 38, 38, 0.1);
  border: 1px solid rgba(220, 38, 38, 0.3);
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 16px;
}

.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 8px;
}

.btn-secondary,
.btn-primary,
.btn-danger {
  padding: 10px 20px;
  border-radius: 10px;
  border: none;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;
}

.btn-secondary {
  background: #f3f4f6;
  color: #374151;
}

.btn-secondary:hover:not(:disabled) {
  background: #e5e7eb;
}

.btn-primary {
  background: linear-gradient(90deg, #0063AD, #2f7cc7);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(90deg, #045a9b, #2a70b3);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 99, 173, 0.3);
}

.btn-danger {
  background: #dc2626;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background: #b91c1c;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
}

.btn-secondary:disabled,
.btn-primary:disabled,
.btn-danger:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.password-field {
  display: flex;
  gap: 8px;
  align-items: stretch;
}

.password-field input {
  flex: 1;
}

.eye-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #d7e3f4;
  background: #f5f8fe;
  border-radius: 10px;
  cursor: pointer;
  padding: 0 14px;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.eye-btn:hover {
  border-color: #0063AD;
  background: #e8f2ff;
}

.eye-icon {
  width: 22px;
  height: 22px;
  display: block;
  opacity: 0.7;
}

.eye-btn:hover .eye-icon {
  opacity: 1;
}

.error {
  background: rgba(220, 38, 38, 0.1);
  border: 1px solid rgba(220, 38, 38, 0.3);
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 14px;
  margin: 0;
}

/* ===================== Hamburger Button ===================== */
.hamburger {
  display: none; /* hidden on desktop */
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 5px;
  width: 40px;
  height: 40px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 8px;
  transition: background 0.2s ease;
  z-index: 1100;
}

.hamburger:hover {
  background: rgba(0, 0, 0, 0.06);
}

.hamburger-line {
  display: block;
  width: 22px;
  height: 2px;
  background: #333;
  border-radius: 2px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: center;
}

.hamburger.is-open .hamburger-line:nth-child(1) {
  transform: translateY(7px) rotate(45deg);
}

.hamburger.is-open .hamburger-line:nth-child(2) {
  opacity: 0;
  transform: scaleX(0);
}

.hamburger.is-open .hamburger-line:nth-child(3) {
  transform: translateY(-7px) rotate(-45deg);
}

/* ===================== Mobile Menu Overlay ===================== */
.mobile-menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
  z-index: 1050;
  display: flex;
  justify-content: flex-end;
}

.mobile-menu {
  width: 240px;
  max-width: 70vw;
  height: 100%;
  background: #fff;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.15);
  padding: 80px 0 32px 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow-y: auto;
}

.mobile-link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 24px;
  color: #333;
  text-decoration: none;
  font-size: 16px;
  font-weight: 500;
  transition: all 0.2s ease;
  border-left: 3px solid transparent;
}

.mobile-link:hover {
  background: rgba(0, 99, 173, 0.06);
  color: #0063AD;
}

.mobile-link.router-link-active {
  background: rgba(0, 99, 173, 0.1);
  color: #0063AD;
  border-left-color: #0063AD;
  font-weight: 600;
}

.mobile-link-icon {
  width: 24px;
  text-align: center;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-icon-svg {
  width: 20px;
  height: 20px;
  display: block;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.mobile-link:hover .mobile-icon-svg,
.mobile-link.router-link-active .mobile-icon-svg {
  opacity: 1;
}

.mobile-divider {
  height: 1px;
  background: rgba(0, 0, 0, 0.08);
  margin: 8px 24px;
}

/* ===================== Mobile Menu Transition ===================== */
.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition: opacity 0.3s ease;
}

.mobile-menu-enter-active .mobile-menu,
.mobile-menu-leave-active .mobile-menu {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.mobile-menu-enter-from {
  opacity: 0;
}

.mobile-menu-enter-from .mobile-menu {
  transform: translateX(100%);
}

.mobile-menu-leave-to {
  opacity: 0;
}

.mobile-menu-leave-to .mobile-menu {
  transform: translateX(100%);
}

/* ===================== Responsive ===================== */
@media (max-width: 768px) {
  .hamburger {
    display: flex;
  }

  /* Desktop Links immer ausblenden auf Mobile (Hamburger ist immer da) */
  .desktop-links {
    display: none;
  }

  .nav-inner {
    min-height: 56px;
    padding: 0 12px;
    gap: 8px;
    grid-template-columns: auto 1fr auto;
  }

  /* === Nicht angemeldet: Logo links, Burger rechts === */
  .brand {
    order: 1;
    display: flex;
    justify-content: flex-start;
  }

  .hamburger {
    order: 3;
  }

  .nav-right {
    order: 2;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  /* === Angemeldet: Burger links, Logo Mitte, Profil rechts === */
  .nav-inner.logged-in .hamburger {
    order: 1;
  }

  .nav-inner.logged-in .brand {
    order: 2;
    justify-content: center;
  }

  .nav-inner.logged-in .nav-right {
    order: 3;
  }

  .logo {
    height: 32px;
  }

  .profile-avatar {
    width: 34px;
    height: 34px;
    font-size: 12px;
  }

  .profile-dropdown {
    min-width: 260px;
  }
}

@media (max-width: 480px) {
  .nav-inner {
    min-height: 50px;
    padding: 0 10px;
    gap: 6px;
  }

  .logo {
    height: 28px;
  }

  .profile-avatar {
    width: 32px;
    height: 32px;
    font-size: 11px;
  }

  .profile-dropdown {
    min-width: 240px;
  }

  .mobile-menu {
    width: 220px;
  }
}

@media (max-width: 390px) {
  .nav-inner {
    min-height: 48px;
    padding: 0 8px;
  }

  .logo {
    height: 26px;
  }

  .profile-avatar {
    width: 30px;
    height: 30px;
    font-size: 10px;
  }

  .profile-dropdown {
    min-width: 230px;
  }

  .mobile-menu {
    width: 200px;
  }
}
</style>


